import isNil from 'lodash/isNil'
import {
  GeckoMeasurement,
  GeckoMeasurementJson,
} from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseString } from 'utils/parsingHelpers'
import { z } from 'zod'

import { GeckoEntryBase } from './geckoEntry'

/**
 * A self-describing serialized `GeckoMeasurement` (`{ num, unit }`) whose unit is
 * a REGISTERED unit id.
 *
 * `.min(1)` is load-bearing: `GeckoMeasurementUnit.GetByID('')` returns the `None`
 * singleton (NOT undefined), so without it an empty-string unit would pass the
 * refine. No explicit `z.ZodType<GeckoMeasurementJson>` annotation — it suppresses
 * Zod v4 inference on the chained schema.
 */
const geckoMeasurementJsonSchema = z.object({
  num: z.number(),
  unit: z
    .string()
    .min(1)
    .refine((id) => GeckoMeasurementUnit.GetByID(id) !== undefined),
})

/**
 * Parse a stored config quantity. Accepts a self-describing measurement
 * (`{ num, unit }`, the current shape) OR a legacy bare number that carried its
 * unit implicitly via `fallbackUnitId` (pre-migration entries). Fails closed
 * (`undefined`) on a malformed shape or an unknown unit id, so a bad value can
 * never reach the calc as a NaN or a silently-wrong unit.
 *
 * `.optional().catch(undefined)` — `.optional()` first so `undefined` is a valid
 * output (Zod v4 requires the catch fallback to be in the schema's output type),
 * then `.catch(undefined)` to fail closed on a malformed value.
 */
const configMeasurementSchema = (fallbackUnitId?: string) =>
  z
    .preprocess(
      (v) => (typeof v === 'number' ? { num: v, unit: fallbackUnitId } : v),
      geckoMeasurementJsonSchema
    )
    .optional()
    .catch(undefined)

/**
 * Rehydrate a stored config quantity into a `GeckoMeasurement`, tolerating
 * legacy bare numbers via `fallbackUnitId` (typically the entry's
 * `thicknessUnitId`). Returns null when the value is absent or unparseable.
 * The single read-path counterpart to storing `GeckoMeasurementJson`.
 */
export function measurementFromConfigJson(
  input: unknown,
  fallbackUnitId?: string
): GeckoMeasurement | null {
  // Never throws: the schema is optional + `.catch(undefined)`.
  const parsed = configMeasurementSchema(fallbackUnitId).parse(input)
  return parsed ? GeckoMeasurement.CreateFromJSON(parsed) : null
}

/** Which measured thickness drives the MAWP calc per API 579. */
export const mawpCalcThicknessSchema = z.enum(['t_mm', 't_am', 't_c'])
export type MawpCalcThicknessValue = z.infer<typeof mawpCalcThicknessSchema>

/**
 * Zod schema for the ffs-configuration entry `data`. Built as a factory because
 * the three thickness fields fall back to the entry's own `thicknessUnitId` when
 * upgrading legacy bare numbers; the metal-loss fields never existed as bare
 * numbers and get no fallback. Every field but `thicknessUnitId` fails closed to
 * `undefined`, so `.parse()` only throws on a bad `thicknessUnitId` string.
 */
const buildBinningConfigSchema = (fallbackUnitId?: string) =>
  z.object({
    // Preferred display/edit unit for the binning UI, and the fallback unit used
    // to upgrade legacy bare-number values into self-describing measurements.
    thicknessUnitId: z.string(),
    uniformThickness: configMeasurementSchema(fallbackUnitId),
    futureCorrosionAllowance: configMeasurementSchema(fallbackUnitId),
    localThinAreaThreshold: configMeasurementSchema(fallbackUnitId),
    /** Forward-looking date the assessment evaluates fitness for (ISO). */
    targetDate: z.string().optional().catch(undefined),
    /** Inspection-date override; defaults to inspection.data.date when absent (ISO). */
    inspectionDate: z.string().optional().catch(undefined),
    /** RSF acceptance target (RSF_a), dimensionless ratio 0–1. */
    rsfa: z.number().optional().catch(undefined),
    /** Which thickness drives the MAWP calc (t_mm / t_am / t_c). */
    selectedMawpCalcThickness: mawpCalcThicknessSchema.optional().catch(undefined),
    /** Metal-loss future corrosion allowance (FCA_ml); self-describing unit. */
    futureCorrosionAllowanceMl: configMeasurementSchema(),
    /** Corrosion rate (CR); self-describing unit (e.g. mm/yr). */
    corrosionRate: configMeasurementSchema(),
    /** Metal-loss corrosion rate (CR_ml); self-describing unit (e.g. mm/yr). */
    corrosionRateMl: configMeasurementSchema(),
  })

export type FfsAnnex4bBinningConfigurationData = z.infer<
  ReturnType<typeof buildBinningConfigSchema>
>

export function buildFfsAnnex4bBinningConfigurationData({
  thicknessUnitId,
  uniformThickness,
  futureCorrosionAllowance,
  localThinAreaThreshold,
  targetDate,
  inspectionDate,
  rsfa,
  selectedMawpCalcThickness,
  futureCorrosionAllowanceMl,
  corrosionRate,
  corrosionRateMl,
}: {
  thicknessUnitId: GeckoMeasurementUnit['id']
  uniformThickness?: GeckoMeasurementJson
  futureCorrosionAllowance?: GeckoMeasurementJson
  localThinAreaThreshold?: GeckoMeasurementJson
  targetDate?: string
  inspectionDate?: string
  rsfa?: number
  selectedMawpCalcThickness?: MawpCalcThicknessValue
  futureCorrosionAllowanceMl?: GeckoMeasurementJson
  corrosionRate?: GeckoMeasurementJson
  corrosionRateMl?: GeckoMeasurementJson
}): FfsAnnex4bBinningConfigurationData {
  return {
    thicknessUnitId,
    ...(!isNil(uniformThickness) ? { uniformThickness } : {}),
    ...(!isNil(futureCorrosionAllowance) ? { futureCorrosionAllowance } : {}),
    ...(!isNil(localThinAreaThreshold) ? { localThinAreaThreshold } : {}),
    ...(!isNil(targetDate) ? { targetDate } : {}),
    ...(!isNil(inspectionDate) ? { inspectionDate } : {}),
    ...(!isNil(rsfa) ? { rsfa } : {}),
    ...(!isNil(selectedMawpCalcThickness)
      ? { selectedMawpCalcThickness }
      : {}),
    ...(!isNil(futureCorrosionAllowanceMl)
      ? { futureCorrosionAllowanceMl }
      : {}),
    ...(!isNil(corrosionRate) ? { corrosionRate } : {}),
    ...(!isNil(corrosionRateMl) ? { corrosionRateMl } : {}),
  }
}

export class GeckoFfsAnnex4bBinningConfiguration extends GeckoEntryBase<
  FfsAnnex4bBinningConfigurationData,
  {
    [RolodexEntryType.ORGANIZATION]: string
    [RolodexEntryType.SITE]: string
    [RolodexEntryType.UNIT]: string
    [RolodexEntryType.COMPONENT]: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    // `thicknessUnitId` is the entry's integrity gate AND the fallback unit for
    // upgrading legacy bare-number measurements, so resolve it before parsing the
    // rest of the data through the (fallback-aware) schema.
    const thicknessUnitId = parseString(json.data.thicknessUnitId)
    const thicknessUnit = thicknessUnitId
      ? GeckoMeasurementUnit.GetByID(thicknessUnitId)
      : undefined
    if (!thicknessUnit) {
      throw new Error(
        `Invalid ffs-configuration entry: ${json.id} is missing a valid thicknessUnitId`
      )
    }

    // Safe: `thicknessUnitId` is a validated string and every other field is
    // `.catch`-guarded, so `.parse` cannot throw here.
    const parsedData: FfsAnnex4bBinningConfigurationData = {
      ...buildBinningConfigSchema(thicknessUnit.id).parse(json.data),
      // Normalize to the canonical unit id (matches the prior output).
      thicknessUnitId: thicknessUnit.id,
    }

    super(
      json,
      parsedData,
      {
        [RolodexEntryType.ORGANIZATION]:
          parseString(json.tags[RolodexEntryType.ORGANIZATION]) ?? '',
        [RolodexEntryType.SITE]:
          parseString(json.tags[RolodexEntryType.SITE]) ?? '',
        [RolodexEntryType.UNIT]:
          parseString(json.tags[RolodexEntryType.UNIT]) ?? '',
        [RolodexEntryType.COMPONENT]:
          parseString(json.tags[RolodexEntryType.COMPONENT]) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoFfsAnnex4bBinningConfiguration {
    return new GeckoFfsAnnex4bBinningConfiguration(json)
  }
}
